import { ReactNode } from "react";
import styled, { IStyledComponent } from "styled-components";
import { SX, Theme, TypographyVariant } from "../../shared/types";
import * as CONSTANTS from "../../shared/constants";
import { buildCSSObjectFromSX } from "../../shared/utils";

type H1Props = JSX.IntrinsicElements["h1"] & {
  children: ReactNode;
  sx?: SX | undefined | null;
};

export const H1Component = styled.h1<H1Props>(({ theme, sx }) => {
  const cssObject = sx ? buildCSSObjectFromSX(sx, theme) : {};
  return {
    fontFamily: theme.FONT.FAMILY.HEADERS,
    fontSize: "2rem",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "44px",
    color: theme.COLORS.GRAY_BLACK,
    ...cssObject,
  };
});

type H2Props = JSX.IntrinsicElements["h2"] & {
  children: ReactNode;
  sx?: SX | undefined | null;
};

export const H2Component = styled.h2<H2Props>(({ theme, sx }) => {
  const cssObject = sx ? buildCSSObjectFromSX(sx, theme) : {};
  return {
    fontFamily: theme.FONT.FAMILY.HEADERS,
    fontSize: "1.625rem",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "36px",
    color: theme.COLORS.GRAY_BLACK,
    ...cssObject,
  };
});

type H3Props = JSX.IntrinsicElements["h3"] & {
  children: ReactNode;
  sx?: SX | undefined | null;
};

export const H3Component = styled.h3<H3Props>(({ theme, sx }) => {
  const cssObject = sx ? buildCSSObjectFromSX(sx, theme) : {};
  return {
    fontFamily: theme.FONT.FAMILY.HEADERS,
    fontSize: "1.25rem",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "28px",
    color: theme.COLORS.GRAY_BLACK,
    ...cssObject,
  };
});

type H4Props = JSX.IntrinsicElements["h4"] & {
  children: ReactNode;
  sx?: SX | undefined | null;
};

export const H4Component = styled.h4<H4Props>(({ theme, sx }) => {
  const cssObject = sx ? buildCSSObjectFromSX(sx, theme) : {};
  return {
    fontFamily: theme.FONT.FAMILY.HEADERS,
    fontSize: "1.125rem",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "25px",
    color: theme.COLORS.GRAY_BLACK,
    ...cssObject,
  };
});

type H5Props = JSX.IntrinsicElements["h5"] & {
  children: ReactNode;
  sx?: SX | undefined | null;
};

export const H5Component = styled.h5<H5Props>(({ theme, sx }) => {
  const cssObject = sx ? buildCSSObjectFromSX(sx, theme) : {};
  return {
    fontFamily: theme.FONT.FAMILY.HEADERS,
    fontSize: "1rem",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "22px",
    color: theme.COLORS.GRAY_BLACK,
    ...cssObject,
  };
});

type PProps = JSX.IntrinsicElements["p"] & {
  children: ReactNode;
  sx?: SX | undefined | null;
};

export const BodyComponent = styled.p<PProps>(({ theme, sx }) => {
  const cssObject = sx ? buildCSSObjectFromSX(sx, theme) : {};
  return {
    fontFamily: theme.FONT.FAMILY.BODY,
    fontSize: "1rem",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "22px",
    color: theme.COLORS.GRAY_BLACK,
    ...cssObject,
  };
});

type SpanProps = JSX.IntrinsicElements["span"] & {
  // TODO: Do we really need variant?
  variant: TypographyVariant;
  children: ReactNode;
  sx?: SX | undefined | null;
};

export const BodySmallComponent = styled.span<SpanProps>(({ theme, sx }) => {
  const cssObject = sx ? buildCSSObjectFromSX(sx, theme) : {};
  return {
    fontFamily: theme.FONT.FAMILY.BODY,
    fontSize: "0.875rem",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "150%",
    color: theme.COLORS.GRAY_BLACK,
    ...cssObject,
  };
});

export const BodyXSmallComponent = styled.span<SpanProps>(({ theme, sx }) => {
  const cssObject = sx ? buildCSSObjectFromSX(sx, theme) : {};
  return {
    fontFamily: theme.FONT.FAMILY.BODY,
    fontSize: "0.75rem",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "17px",
    color: theme.COLORS.GRAY_BLACK,
    ...cssObject,
  };
});

export const BodyBoldComponent = styled.span<SpanProps>(({ theme, sx }) => {
  const cssObject = sx ? buildCSSObjectFromSX(sx, theme) : null;
  return {
    fontFamily: theme.FONT.FAMILY.TEXT_BOLD,
    fontSize: "1rem",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "22px",
    color: theme.COLORS.GRAY_BLACK,
    ...cssObject,
  };
});

export const BodySmallBoldComponent = styled.span<SpanProps>(
  ({ theme, sx }) => {
    const cssObject = sx ? buildCSSObjectFromSX(sx, theme) : {};
    return {
      fontFamily: theme.FONT.FAMILY.TEXT_BOLD,
      fontSize: "0.875rem",
      fontStyle: "normal",
      fontWeight: 700,
      lineHeight: "19px",
      color: theme.COLORS.GRAY_BLACK,
      ...cssObject,
    };
  },
);

export const BodyXSmallBoldComponent = styled.span<SpanProps>(
  ({ theme, sx }) => {
    const cssObject = sx ? buildCSSObjectFromSX(sx, theme) : {};
    return {
      fontFamily: theme.FONT.FAMILY.TEXT_BOLD,
      fontSize: "0.75rem",
      fontStyle: "normal",
      fontWeight: 700,
      lineHeight: "19px",
      color: theme.COLORS.GRAY_BLACK,
      ...cssObject,
    };
  },
);

const COMPONENT_MAPPING = {
  h1: H1Component,
  h2: H2Component,
  h3: H3Component,
  h4: H4Component,
  h5: H5Component,
  body: BodyComponent,
  "body-small": BodySmallComponent,
  "body-x-small": BodyXSmallComponent,
  "body-bold": BodyBoldComponent,
  "body-small-bold": BodySmallBoldComponent,
  "body-x-small-bold": BodyXSmallBoldComponent,
} as const;

type TypographyProps =
  | H1Props
  | H2Props
  | H3Props
  | H4Props
  | H5Props
  | PProps
  | SpanProps;

export const Typography = (
  props: TypographyProps & {
    variant: TypographyVariant;
    color?: keyof Theme["COLORS"];
    mt?: number;
    testId?: string;
    dataTestId?: string;
    insensitive?: boolean;
    underline?: boolean;
    sx?: SX | undefined;
  } & (
      | {
          sensitiveData?: false;
          isDemo?: boolean;
        }
      | {
          sensitiveData?: true;
          isDemo: boolean;
        }
    ),
) => {
  const theme: Theme = CONSTANTS.defaultTheme; // TODO: Use theme provider like this; const theme = useContext(ThemeContext);
  const Component = COMPONENT_MAPPING[props.variant] as IStyledComponent<
    // TODO replace any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    any,
    // TODO replace any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    any
  >;
  if (!Component) return null;
  const {
    sensitiveData,
    sx,
    children,
    insensitive,
    underline,
    isDemo,
    dataTestId,
    testId,
    ...restProps
  } = props;

  let isSensitive = true;
  if (insensitive !== undefined) {
    isSensitive = !insensitive;
  } else if (sensitiveData !== undefined) {
    isSensitive = sensitiveData;
  }

  const isDemoOrDefault = isDemo ?? false;
  const text =
    children && isDemoOrDefault && isSensitive
      ? CONSTANTS.DEMO
      : (children as string);

  const computedProps = {
    ...restProps,
    style: {
      color: props.color ? theme.COLORS[props.color] : null,
      ...(props?.mt && { marginTop: props.mt }),
      ...(underline && { textDecoration: "underline" }),
      ...restProps.style,
    },
  };

  return (
    <Component
      {...computedProps}
      {...((testId || dataTestId) && {
        "data-testid": testId || dataTestId,
      })}
      sx={sx}
    >
      {text}
    </Component>
  );
};
